import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import toast from 'react-hot-toast'
import * as Yup from 'yup'
import { BsCheck2 } from 'react-icons/bs'
import { IoCopy } from 'react-icons/io5'
import QRCode from 'react-qr-code'
import {
  useGet2FASetupMutation,
  useConfirmSetupMutation,
} from '../../../features/2fa/twoFactorSlice'
import { Loader } from '../../../shared/Loader'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentToken,
  selectCurrentUser,
  selectRefreshToken,
  setCredentials,
} from '../../../features/auth/authSlice'

export const Enable2FAForm = () => {
  const dispatch = useDispatch()

  const token = useSelector(selectCurrentToken)
  const user = useSelector(selectCurrentUser)
  const refreshToken = useSelector(selectRefreshToken)

  const [getSetup, { isLoading: isGetSetupLoading, data }] =
    useGet2FASetupMutation()
  // copy to clipboard state -----------
  const [copy, setCopy] = useState<{ value: string; copied: boolean }>({
    value: '',
    copied: false,
  })

  const [confirmSetup, { isLoading: isConfirmSetupLoading, isSuccess }] =
    useConfirmSetupMutation()

  useEffect(() => {
    getSetup()
    if (data) {
      setCopy((prevState) => ({ ...prevState, value: data?.secret }))
    }
  }, [])

  const formik = useFormik({
    initialValues: {
      code: null,
    },
    validationSchema: Yup.object().shape({
      code: Yup.number().required('Provide the code to verify'),
    }),
    onSubmit: async (values) => {
      try {
        toast.loading('Setting up two factor authentication...')
        await confirmSetup({ code: values.code }).unwrap()
        toast.dismiss()
        formik.resetForm()
        // user object modification
        const newUser = { ...user, is2FAEnabled: true }
        dispatch(setCredentials({ user: newUser, token, refreshToken }))

        toast.success('Two factor authentication is enabled!')
      } catch (error: any) {
        toast.dismiss()
        console.log(error?.data.message)
        toast.error(error?.data.message)
      }
    },
  })

  return (
    <div className='flex flex-col items-center gap-6'>
      <div className='flex flex-col gap-2 px-4 py-3 bg-gray-600 rounded-md'>
        <div className='flex items-center gap-3'>
          <div className='w-2 h-2 rounded-full bg-amber-100'></div>
          <p className='text-sm text-white/40'>
            Install a compatible app on your mobile device
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='w-2 h-2 rounded-full bg-amber-100'></div>
          <p className='text-sm text-white/40'>Scan the following QR code</p>
        </div>
      </div>

      <div className='h-64 w-64 border-[10px] border-white box-content rounded-md bg-gray-500 text-white text-center mt-4'>
        {isGetSetupLoading ? (
          <div className='h-full w-full flex justify-center items-center'>
            <Loader />
          </div>
        ) : (
          <QRCode width='100%' height='auto' value={data?.url || ''} />
        )}
      </div>

      <CopyToClipboard
        text={data?.secret}
        onCopy={() => setCopy({ ...copy, copied: true })}
      >
        <div className='transition-all duration-200 w-full max-w-sm flex justify-between items-center bg-gray-700 hover:bg-gray-700/50 px-4 py-2 rounded-lg cursor-pointer'>
          <div className='flex flex-col gap-1'>
            <p className='text-xs text-white/40'>Tap to copy 2FA secret</p>
            <p className='text-sm text-white/80'>
              {isGetSetupLoading ? (
                <div className='h-3 w-[150px] bg-zinc-600 animate-pulse' />
              ) : (
                data?.secret
              )}
            </p>
          </div>
          {copy.copied ? (
            <BsCheck2 className='text-2xl text-green-500' />
          ) : (
            <IoCopy className='text-2xl text-white/80' />
          )}
        </div>
      </CopyToClipboard>

      <form
        className='flex flex-col gap-2 mt-4 w-full max-w-sm'
        onSubmit={formik.handleSubmit}
      >
        <label htmlFor='code' className='text-xs text-white/70'>
          Type the 2FA token below for verification
        </label>
        <input
          type='number'
          name='code'
          id='code'
          placeholder=''
          value={formik.values.code || ''}
          onChange={formik.handleChange}
          onWheel={(e) => e.currentTarget.blur()}
          className='form-input spin-button-none '
        />

        <p className='text-red-500 text-xs'>
          {formik.touched.code &&
            Boolean(formik.errors.code) &&
            formik.errors.code}
        </p>
        <button
          type='submit'
          className='btn-primary px-4 py-2 rounded-md w-full max-w-sm'
        >
          Submit
        </button>
      </form>
    </div>
  )
}
